import { useNavigate, useLocation, To } from 'react-router-dom';

/**
 * A custom navigation hook that persists query parameters across navigations.
 */
export const usePersistentNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigates to a new path while preserving the current query string.
   * @param to The destination path or a partial path object.
   * @param options Navigation options (e.g., replace, state).
   */
  const persistentNavigate = (to: To, options?: { replace?: boolean; state?: any }) => {
    let targetPathname = '';
    let targetSearch = '';
    let targetHash = '';

    if (typeof to === 'string') {
      // Create a dummy URL to parse relative paths
      const url = new URL(to, 'http://dummy.com');
      targetPathname = url.pathname;
      targetSearch = url.search;
      targetHash = url.hash;
    } else {
      targetPathname = to.pathname || '';
      targetSearch = to.search || '';
      targetHash = to.hash || '';
    }

    // Merge existing search params with new ones
    // We want to keep existing params (like stagingEnv) unless they are explicitly overridden or cleared?
    // The previous logic was `search: to.search || location.search`. This implied if new search exists, it REPLACES old search.
    // However, usually "persist" means keep the persistent ones (stagingEnv). 
    // The error we faced was `pathname` including `?`.

    // Let's implement smart merging: Keep 'stagingEnv' specifically, or merge all?
    // The prompt says "persists query parameters". 
    // Let's merge all existing params into the new ones, preferring new ones if conflict.

    const combinedParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams(targetSearch);

    // List of parameters that should NOT be persisted across different routes
    // unless they are explicitly provided in the new targetSearch.
    const transientParams = ['case_id', 'from', 'tab', 'page', 'rowsPerPage', 'userType', 'hideInternal'];

    // If we are navigating to a different pathname, remove transient params
    // that aren't explicitly provided in the new path's search string.
    if (targetPathname !== location.pathname) {
      transientParams.forEach(param => {
        if (!newParams.has(param)) {
          combinedParams.delete(param);
        }
      });
    }

    newParams.forEach((value, key) => {
      combinedParams.set(key, value);
    });

    const destination = {
      pathname: targetPathname,
      search: combinedParams.toString() ? `?${combinedParams.toString()}` : '',
      hash: targetHash
    };

    navigate(destination, options);
  };

  return persistentNavigate;
};
